import type { AuthState } from 'AuthBff/types';
import { renderSignInButton, renderSignUpButton } from 'BomdisoftComponents/account/auth-buttons/render';
import { renderUserAvatarMenu, type UserAvatarMenuItemValue } from 'BomdisoftComponents/account/user-avatar-menu/render';
import { type HtmlValue, html } from 'BomdisoftComponents/html/index';
import { PERSON_ROUNDED_GLYPH } from 'BomdisoftComponents/icons/glyphs';
import { renderIcon } from 'BomdisoftComponents/icons/render';
import { renderSkeleton } from 'BomdisoftComponents/skeletons/render';
import type { TooltipPlacement } from 'BomdisoftComponents/tooltip/render';

export type AuthControlsOptions = {
  accountHref?: string;
  accountsSection?: HtmlValue;
  items?: readonly UserAvatarMenuItemValue[];
  leadingItems?: readonly UserAvatarMenuItemValue[];
  loadingView?: 'signed-out' | 'skeleton';
  logoutHref?: string;
  manageAccountLabel: string;
  menuId?: string;
  menuLabel?: string;
  showUserName?: boolean;
  signInHref?: string;
  signInLabel: string;
  signOutLabel: string;
  signUpClassName?: string;
  signUpHref?: string;
  signUpLabel: string;
  tooltipId?: string;
  tooltipPlacement?: TooltipPlacement;
};

export const AUTH_CONTROLS_DEFAULT_LOGOUT_HREF = '/api/auth/logout';

export const renderAuthControls = (options: AuthControlsOptions, state: AuthState) => {
  const {
    accountHref,
    accountsSection,
    items = [],
    leadingItems = [],
    loadingView = 'signed-out',
    manageAccountLabel,
    menuId = 'auth-controls-menu',
    menuLabel,
    showUserName,
    signInHref = '/api/auth/login',
    signInLabel,
    signOutLabel,
    signUpClassName,
    signUpHref = '/api/auth/login?prompt=create',
    signUpLabel,
    tooltipId = 'auth-controls-tooltip',
    tooltipPlacement,
  } = options;
  const signedOut = html`${renderSignInButton({ annotation: 'Sign in', href: signInHref, label: signInLabel })}${renderSignUpButton({ annotation: 'Create an account', className: signUpClassName, href: signUpHref, label: signUpLabel })}`;
  if (state.status === 'loading') return loadingView === 'skeleton' ? renderSkeleton({ height: 28, variant: 'circular', width: 28 }) : signedOut;
  if (state.status === 'unauthenticated') return signedOut;
  const menuItems = [...leadingItems.map(item => ({ ...item, external: true })), ...(accountHref ? [{ annotation: 'Manage account', external: true, href: accountHref, icon: renderIcon(PERSON_ROUNDED_GLYPH), label: manageAccountLabel, testId: 'auth-controls-account' }] : []), ...items];
  return renderUserAvatarMenu({ accountsSection, avatarUrl: state.user.picture ?? undefined, email: state.user.email, items: menuItems, menuId, menuLabel, name: state.user.name, showName: showUserName, signOutAnnotation: 'Sign out', signOutLabel, tooltipId, tooltipPlacement });
};
